import { PbsAdUnit } from '@pubstack/common/src/pbs/adunit'
import { type FunctionComponent, useCallback, useState } from 'react'
import { useUpdateAdUnit } from '~/api/pbs/ad-units.api'
import Button from '~/components/Button'
import { ConfirmationPhraseField, isPhraseTyped } from '~/components/ConfirmationPhraseField'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { Modal } from '~/components/Modal'

type Props = {
  adUnit: PbsAdUnit
}

/**
 * The confirmation for archiving a Node ad unit.
 *
 * The name has to be typed back before Archive opens: the action is reached from a row in a list, where
 * the cheapest mistake is archiving the ad unit next to the one meant, and typing the name is what makes
 * the viewer read which row they are on. It is reversible, but not by the person clicking — unarchiving
 * is Pubstack-only, and saying so here is what stops "I'll just put it back" being discovered as a 403
 * later. Same gate, and the same copy, as archiving a media.
 */
export const ArchiveAdUnitModal: FunctionComponent<Props> = ({ adUnit }) => {
  // API Hooks
  const { mutateAsync: updateAdUnit, isPending: isUpdatePending } = useUpdateAdUnit(adUnit.id, {
    successToast: (
      <>
        <strong>{adUnit.name}</strong> has been archived successfully.
      </>
    ),
    errorToast: 'An error occurred while trying to archive the ad unit.',
  })

  // Hooks
  const { close } = useGlobalModal()
  const [typedName, setTypedName] = useState('')
  const isNameConfirmed = isPhraseTyped(adUnit.name, typedName)

  const handleArchive = useCallback(async () => {
    // The disabled button is the affordance; not archiving is the invariant.
    if (isUpdatePending || !isNameConfirmed) return

    await updateAdUnit({ ...adUnit, enabled: false })
    close()
  }, [adUnit, close, isNameConfirmed, isUpdatePending, updateAdUnit])

  return (
    <Modal.Content>
      <Modal.Title>Archive ad unit</Modal.Title>
      <Modal.Body>
        <div className={'flex flex-col gap-3'}>
          <p>
            You are about to archive <strong>{adUnit.name}</strong>.
          </p>
          <p>Once archived, the ad unit will be removed from all mapped integrations and stop monetising. Please ensure to re-deploy your stack and remove the code from pbjs.config.</p>
          <p>Archived ad units stay on a separate page, but only Pubstack can bring one back.</p>
          <ConfirmationPhraseField phrase={adUnit.name} value={typedName} onChange={setTypedName} disabled={isUpdatePending} trackingLabel={'pbs-ad-units-archive-type'} />
        </div>
      </Modal.Body>
      <Modal.Actions>
        <Button variant="tertiary" onClick={close} trackingLabel="pbs-ad-units-archive-cancel">
          Cancel
        </Button>
        <Button
          variant="negative"
          iconName={isUpdatePending ? 'pubstack' : 'archive'}
          iconSpin={isUpdatePending}
          disabled={isUpdatePending || !isNameConfirmed}
          onClick={handleArchive}
          trackingLabel="pbs-ad-units-archive-confirm"
        >
          {isUpdatePending ? 'Archiving ad unit...' : 'Archive ad unit'}
        </Button>
      </Modal.Actions>
    </Modal.Content>
  )
}
